import { QuestionIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { FC, ReactNode, useContext } from 'react';
import { Variant } from 'react-bootstrap/esm/types';

import { Tooltip } from 'waldur-ui';

import { StaffOnlyIndicator } from '@/customer/details/StaffOnlyIndicator';
import { ResourceAction } from '@/marketplace/resources/actions/constants';
import { ResourceActionMenuContext } from '@/marketplace/resources/actions/ResourceActionMenuContext';
import { ActionButton } from '@/table/ActionButton';
import { ActionsDropdownItem, PlainActionItem } from '@/table/ActionsDropdown';
import { CompactActionButton } from '@/table/CompactActionButton';

export interface ActionItemProps {
  title: string;
  action: () => void;
  iconNode?: ReactNode;
  iconColor?: Variant;
  staff?: boolean;
  important?: boolean;
  className?: string;
  disabled?: boolean;
  tooltip?: string;
  as?;
  size?: 'sm' | 'lg';
  actionId?: ResourceAction;
  resource?: any;
  variant?: string;
}

export const ActionItem: FC<ActionItemProps> = (props) => {
  const actionMenuContext = useContext(ResourceActionMenuContext);
  // Default to ActionsDropdownItem (a real RadixDropdownMenu.Item, needing
  // a Menu Root/Content ancestor); ModalActionsDialog's "show all actions"
  // search results render outside one entirely (a plain react-bootstrap
  // Modal, not a Radix panel), so notInMenu switches every row in that
  // tree to PlainActionItem — same appearance, zero Radix dependency.
  const Component =
    props.as ||
    (actionMenuContext?.notInMenu ? PlainActionItem : ActionsDropdownItem);
  const isMenuRow =
    Component === ActionsDropdownItem || Component === PlainActionItem;
  if (
    props.actionId &&
    props.resource?.offering_plugin_options?.disabled_resource_actions?.includes(
      props.actionId,
    )
  ) {
    return null;
  }
  if (
    actionMenuContext?.query &&
    !props.title
      .toLocaleLowerCase()
      .includes(actionMenuContext.query.toLocaleLowerCase())
  ) {
    return null;
  }
  if (actionMenuContext?.hideDisabled && props.disabled) {
    return null;
  }
  if (actionMenuContext?.hideNonImportant && !props.important) {
    return null;
  }

  // When rendering as a Button (or any non-menu-item), use ActionButton or CompactActionButton
  if (props.as && !isMenuRow) {
    const ButtonComponent =
      props.size === 'sm' ? CompactActionButton : ActionButton;
    return (
      <div className="d-flex align-items-center">
        <ButtonComponent
          className={props.className}
          action={props.action}
          disabled={props.disabled}
          iconNode={props.iconNode}
          title={props.title}
          tooltip={props.tooltip}
          variant={props.variant}
        />
        {props.staff && <StaffOnlyIndicator className="text-dark ms-1 me-3" />}
      </div>
    );
  }

  return isMenuRow ? (
    <div className="d-flex align-items-center">
      <Component
        className={classNames(
          'd-flex gap-3',
          props.className,
          props.disabled && 'bg-hover-lighten',
        )}
        // onSelect, not onClick: both Component variants accept it —
        // ActionsDropdownItem is Radix's own prop, closing the menu and
        // returning focus to the trigger on its own; PlainActionItem
        // (notInMenu) has no menu to close, so it just forwards onSelect
        // as a plain click handler. Either way it covers keyboard
        // activation (Enter/Space) as well as pointer. `disabled` blocks
        // selection while leaving the row hoverable so its explanatory
        // tooltip still shows.
        onSelect={() => props.action()}
        disabled={props.disabled}
      >
        <div
          className={props.disabled ? 'opacity-50' : undefined}
          data-testid="action-item-content"
        >
          {props.iconNode && (
            <span
              className={classNames(
                'svg-icon svg-icon-2',
                `svg-icon-${props.iconColor || 'gray-400'}`,
              )}
            >
              {props.iconNode}
            </span>
          )}
          {props.title}
        </div>
      </Component>
      {props.tooltip && (
        <Tooltip label={props.tooltip}>
          <QuestionIcon
            size={20}
            weight="bold"
            className={classNames(
              'ms-1 me-3 text-muted',
              props.disabled && 'opacity-50',
            )}
          />
        </Tooltip>
      )}
      {props.staff && <StaffOnlyIndicator className="text-dark ms-1 me-3" />}
    </div>
  ) : (
    <Component {...props} />
  );
};
